import { HeartIcon, BuildingStorefrontIcon, PencilIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '../../contexts/AuthContext'
import USER_TYPE from '../../constants/userType'
import { CONTENT_TYPES } from '../../constants/contentTypes'
import ReportButton from '../common/ReportButton'
import ImageUploadModal from './ImageUploadModal'
import AddRetailerToFavorites from '../Favorites/AddRetailerToFavourites'
import DisplayShopName from '../common/DisplayShopName'
import ShopLocation from '../common/ShopLocation'

export default function RetailerShopCard({ shopData, styleClass = 'mt-4', editable = false }) {
  const { currentUser } = useAuth()
  const userType = currentUser?.userType || USER_TYPE.guest
  const navigate = useNavigate()
  const [isImageModalOpen, setIsImageModalOpen] = useState(false)

  const isWholesellerShop = shopData?.userType === USER_TYPE.wholeseller

  if (!shopData) {
    return
  }

  return (
    <div
      className={`bg-background rounded-xl shadow-md border border-gray-100 mx-auto overflow-hidden group hover:shadow-lg transition-all duration-300 ${styleClass}`}
    >
      <div className="p-3 md:p-4">
        {/* Main content - responsive layout */}
        <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
          {/* Left side: Profile + Info */}
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3 text-center sm:text-left flex-1">
            {/* Shop Profile Image */}
            <div className="relative flex-shrink-0">
              {shopData.shopPicture ? (
                <img
                  src={shopData.shopPicture}
                  alt={shopData.shopName}
                  className="w-14 h-14 md:w-16 md:h-16 rounded-full object-cover border-2 border-gray-200 shadow-sm"
                />
              ) : (
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-gray-100 border-2 border-gray-200 flex items-center justify-center shadow-sm">
                  <BuildingStorefrontIcon className="w-7 h-7 md:w-8 md:h-8 text-gray-400" />
                </div>
              )}
            </div>

            {/* Shop Information */}
            <div className="flex-1 min-w-0 space-y-1">
              <h2 className="text-base md:text-lg font-bold text-gray-900 break-words leading-tight flex justify-center sm:justify-start">
                <DisplayShopName
                  shop={{
                    isVerified: shopData.isApproved,
                    isPremium: shopData.isPremium,
                    name: shopData.shopName,
                  }}
                  size="lg"
                />
              </h2>
              <p className="text-gray-500 text-xs md:text-sm leading-relaxed ">
                {shopData.description ||
                  `At Sokooni, we are committed to providing quality products and exceptional service to our customers. Browse ${shopData.shopName}'s selection today.`}
              </p>
            </div>
          </div>

          {/* Right side: Shop Details + Actions */}
          <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3 md:gap-4 md:flex-shrink-0">
            {/* Shop Details - compact layout on larger screens */}
            <div className="bg-gray-50 rounded-lg p-2 md:p-3 space-y-2 md:space-y-1 md:min-w-[200px]">
              <ShopLocation
                shop={{
                  shopNumber: shopData.shopNumber,
                  floor: shopData.floor,
                  buildingName: shopData.buildingName,
                }}
                layout="stacked"
                iconColor="text-secondary-600"
                className="text-gray-800 text-xs md:text-sm"
              />
              {!isWholesellerShop && (
                <div className="flex items-center gap-2 text-xs md:text-sm">
                  <HeartIcon className="text-secondary-600 w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
                  <span className="text-gray-800">{shopData.favoriteCount} Favorites</span>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex flex-col gap-2 md:w-auto">
              {/* User action buttons */}
              {userType === USER_TYPE.customer && !isWholesellerShop && (
                <div className="flex items-center gap-2 justify-center md:justify-start">
                  <AddRetailerToFavorites
                    customerId={currentUser?.id}
                    retailer={{
                      id: shopData.userId,
                      shopSlug: shopData.shopSlug,
                      isFavorite: shopData.isFavorite,
                    }}
                  />
                  <ReportButton
                    contentId={shopData?.userId}
                    contentType={CONTENT_TYPES.retailer}
                    reportTarget={shopData?.shopName}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {editable && (
          <div className="flex gap-2">
            <button
              onClick={() => setIsImageModalOpen(true)}
              className="bg-tertiary-50 text-tertiary-500  px-3 py-2 rounded-lg font-medium transition-all duration-200 shadow-sm text-xs md:text-sm whitespace-nowrap"
              title="Edit Shop Image"
            >
              Edit Shop Image
            </button>
            <button
              onClick={() => navigate(`/my-shop/edit`)}
              className="bg-secondary-600 text-white px-3 py-2 rounded-lg font-medium hover:bg-secondary-600/90 transition-all duration-200 shadow-sm text-xs md:text-sm whitespace-nowrap"
            >
              Edit Shop
            </button>
          </div>
        )}
      </div>

      {editable && (
        <ImageUploadModal
          isOpen={isImageModalOpen}
          onClose={() => setIsImageModalOpen(false)}
          currentImage={shopData.shopPicture}
          onImageUpdate={() => {}}
        />
      )}
    </div>
  )
}
